import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  IndianRupee, Plus, Calculator, FileText, RefreshCw, Trash2, Building2, Pencil
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { usePermissions } from "../hooks/usePermissions";

const formatINR = (v) => {
  if (!v) return "0";
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)} L`;
  return n.toLocaleString("en-IN");
};

const PricingPage = () => {
  const { canEdit } = usePermissions();
  const [tab, setTab] = useState("rules");
  const [rules, setRules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [form, setForm] = useState({
    project_id: "", project_name: "", base_price_per_sqft: "", floor_rise_per_floor: "",
    floor_rise_start: "1", parking_charge: "", gst_pct: "5", stamp_duty_pct: "5",
    registration_pct: "1", legal_charges: "", maintenance_deposit: "", infra_charges: "",
    facing_garden: "", facing_park: "", facing_road: "",
  });

  // Calculator state
  const [calcForm, setCalcForm] = useState({
    project_id: "", carpet_area: "", super_buildup_area: "", floor: "", facing: "", parking_slots: "1",
  });
  const [calcResult, setCalcResult] = useState(null);

  // Price sheet state
  const [sheetForm, setSheetForm] = useState({ unit_id: "" });
  const [sheetResult, setSheetResult] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rulesRes, projRes] = await Promise.all([
        axios.get(`${API}/pricing/rules`),
        axios.get(`${API}/projects`),
      ]);
      setRules(rulesRes.data);
      setProjects(projRes.data);
    } catch { toast.error("Failed to load pricing data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const createRule = async () => {
    try {
      await axios.post(`${API}/pricing/rules`, {
        project_id: form.project_id,
        project_name: form.project_name,
        base_price_per_sqft: Number(form.base_price_per_sqft),
        floor_rise_per_floor: Number(form.floor_rise_per_floor),
        floor_rise_start: Number(form.floor_rise_start),
        parking_charge: Number(form.parking_charge),
        gst_pct: Number(form.gst_pct),
        stamp_duty_pct: Number(form.stamp_duty_pct),
        registration_pct: Number(form.registration_pct),
        legal_charges: Number(form.legal_charges),
        maintenance_deposit: Number(form.maintenance_deposit),
        infra_charges: Number(form.infra_charges),
        facing_premiums: {
          garden: Number(form.facing_garden) || 0,
          park: Number(form.facing_park) || 0,
          road: Number(form.facing_road) || 0,
        },
      });
      toast.success("Pricing rule created");
      setAddOpen(false);
      fetchData();
    } catch { toast.error("Failed to create rule"); }
  };

  const deleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/pricing/rules/${ruleId}`);
      toast.success("Rule deleted");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const calculatePrice = async () => {
    try {
      const res = await axios.post(`${API}/pricing/calculate`, {
        project_id: calcForm.project_id,
        carpet_area: Number(calcForm.carpet_area),
        super_buildup_area: Number(calcForm.super_buildup_area),
        floor: Number(calcForm.floor),
        facing: calcForm.facing,
        parking_slots: Number(calcForm.parking_slots),
      });
      setCalcResult(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Calculation failed"); }
  };

  const saveEditRule = async () => {
    if (!editForm) return;
    try {
      await axios.put(`${API}/pricing/rules/${editForm.rule_id}`, {
        base_price_per_sqft: Number(editForm.base_price_per_sqft),
        floor_rise_per_floor: Number(editForm.floor_rise_per_floor),
        floor_rise_start: Number(editForm.floor_rise_start || 1),
        parking_charge: Number(editForm.parking_charge),
        gst_pct: Number(editForm.gst_pct), stamp_duty_pct: Number(editForm.stamp_duty_pct),
        registration_pct: Number(editForm.registration_pct), legal_charges: Number(editForm.legal_charges || 0),
        maintenance_deposit: Number(editForm.maintenance_deposit || 0), infra_charges: Number(editForm.infra_charges || 0),
        facing_garden: Number(editForm.facing_garden || 0), facing_park: Number(editForm.facing_park || 0),
        facing_road: Number(editForm.facing_road || 0),
      });
      toast.success("Rule updated");
      setEditOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update rule"); }
  };

  const generateSheet = async () => {
    try {
      const res = await axios.post(`${API}/pricing/price-sheet`, { unit_id: sheetForm.unit_id });
      setSheetResult(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate"); }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="pricing-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Pricing Engine</h1>
            <p className="text-sm text-slate-500 mt-1">Base pricing, add-ons, calculators & price sheets</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-3 h-10" data-testid="pricing-tabs">
            <TabsTrigger value="rules">Pricing Rules</TabsTrigger>
            <TabsTrigger value="calculator">Price Calculator</TabsTrigger>
            <TabsTrigger value="sheet">Price Sheet</TabsTrigger>
          </TabsList>

          {/* RULES TAB */}
          <TabsContent value="rules" className="space-y-4 mt-4">
            <div className="flex justify-end">
              <Button onClick={() => setAddOpen(true)} data-testid="add-rule-btn"><Plus className="w-4 h-4 mr-1" /> Add Rule</Button>
            </div>

            {loading ? <div className="text-center py-12 text-slate-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></div> :
            rules.length === 0 ? <Card><CardContent className="p-12 text-center text-slate-400"><IndianRupee className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No pricing rules yet</p></CardContent></Card> : (
              <div className="space-y-3">
                {rules.map(r => (
                  <Card key={r.rule_id} data-testid={`rule-card-${r.rule_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-900">{r.project_name || r.project_id}</p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                            <span>Base: {formatINR(r.base_price_per_sqft)}/sqft</span>
                            <span>Floor Rise: {formatINR(r.floor_rise_per_floor)}/floor/sqft</span>
                            <span>Parking: {formatINR(r.parking_charge)}/slot</span>
                            <span>GST: {r.gst_pct}%</span>
                            <span>Stamp Duty: {r.stamp_duty_pct}%</span>
                          </div>
                          {r.facing_premiums && Object.keys(r.facing_premiums).some(k => r.facing_premiums[k]) && (
                            <div className="flex gap-2 mt-1">
                              {Object.entries(r.facing_premiums).filter(([, v]) => v).map(([k, v]) => (
                                <Badge key={k} variant="outline" className="text-xs capitalize">{k}: +{formatINR(v)}/sqft</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          {canEdit("edit_pricing_rules") && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { setEditForm({...r, facing_garden: r.facing_premiums?.garden || "", facing_park: r.facing_premiums?.park || "", facing_road: r.facing_premiums?.road || ""}); setEditOpen(true); }} data-testid={`edit-rule-${r.rule_id}`}><Pencil className="w-4 h-4 text-slate-400" /></Button>
                          )}
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteRule(r.rule_id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* CALCULATOR TAB */}
          <TabsContent value="calculator" className="mt-4">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base">Price Calculator</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Project</label>
                    <Select value={calcForm.project_id} onValueChange={(v) => setCalcForm({ ...calcForm, project_id: v })}>
                      <SelectTrigger data-testid="calc-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-sm font-medium mb-1 block">Carpet Area (sqft)</label><Input type="number" value={calcForm.carpet_area} onChange={(e) => setCalcForm({ ...calcForm, carpet_area: e.target.value })} /></div>
                    <div><label className="text-sm font-medium mb-1 block">Super B/U Area</label><Input type="number" value={calcForm.super_buildup_area} onChange={(e) => setCalcForm({ ...calcForm, super_buildup_area: e.target.value })} data-testid="calc-area" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><label className="text-sm font-medium mb-1 block">Floor</label><Input type="number" value={calcForm.floor} onChange={(e) => setCalcForm({ ...calcForm, floor: e.target.value })} data-testid="calc-floor" /></div>
                    <div><label className="text-sm font-medium mb-1 block">Facing</label>
                      <Select value={calcForm.facing} onValueChange={(v) => setCalcForm({ ...calcForm, facing: v })}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{["east", "west", "north", "south", "garden", "road", "park"].map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><label className="text-sm font-medium mb-1 block">Parking</label><Input type="number" value={calcForm.parking_slots} onChange={(e) => setCalcForm({ ...calcForm, parking_slots: e.target.value })} /></div>
                  </div>
                  <Button className="w-full" onClick={calculatePrice} disabled={!calcForm.project_id} data-testid="calc-btn">
                    <Calculator className="w-4 h-4 mr-2" /> Calculate
                  </Button>
                </CardContent>
              </Card>

              {calcResult ? (
                <Card className="border-2 border-blue-200">
                  <CardHeader><CardTitle className="text-base">Price Breakdown</CardTitle></CardHeader>
                  <CardContent className="space-y-2" data-testid="calc-result">
                    <div className="space-y-1 text-sm">
                      <Row label="Base Price" val={formatINR(calcResult.breakdown.base_price)} sub={`${calcResult.breakdown.area_sqft} sqft × ${formatINR(calcResult.breakdown.base_price_per_sqft)}/sqft`} />
                      <Row label="Floor Rise" val={formatINR(calcResult.breakdown.floor_rise_total)} sub={`Floor ${calcResult.breakdown.floor}`} />
                      <Row label="Facing Premium" val={formatINR(calcResult.breakdown.facing_premium_total)} sub={calcResult.breakdown.facing || "N/A"} />
                      <Row label="Parking" val={formatINR(calcResult.breakdown.parking_total)} sub={`${calcResult.breakdown.parking_slots} slot(s)`} />
                      <div className="border-t pt-2 mt-2"><Row label="Agreement Value" val={formatINR(calcResult.agreement_value)} bold /></div>
                      <Row label={`GST (${calcResult.taxes_and_charges.gst_pct}%)`} val={formatINR(calcResult.taxes_and_charges.gst)} />
                      <Row label={`Stamp Duty (${calcResult.taxes_and_charges.stamp_duty_pct}%)`} val={formatINR(calcResult.taxes_and_charges.stamp_duty)} />
                      <Row label={`Registration (${calcResult.taxes_and_charges.registration_pct}%)`} val={formatINR(calcResult.taxes_and_charges.registration)} />
                      {calcResult.taxes_and_charges.legal_charges > 0 && <Row label="Legal Charges" val={formatINR(calcResult.taxes_and_charges.legal_charges)} />}
                      {calcResult.taxes_and_charges.maintenance_deposit > 0 && <Row label="Maintenance Deposit" val={formatINR(calcResult.taxes_and_charges.maintenance_deposit)} />}
                      {calcResult.taxes_and_charges.infra_charges > 0 && <Row label="Infra Charges" val={formatINR(calcResult.taxes_and_charges.infra_charges)} />}
                    </div>
                    <div className="bg-slate-900 text-white rounded-lg p-4 mt-3 text-center">
                      <p className="text-xs text-slate-300">Total Cost</p>
                      <p className="text-2xl font-bold font-['Manrope']">{formatINR(calcResult.total_cost)}</p>
                      <p className="text-xs text-slate-400 mt-1">Effective: {formatINR(calcResult.effective_price_per_sqft)}/sqft</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card><CardContent className="p-12 text-center text-slate-400"><Calculator className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>Fill in details and calculate</p></CardContent></Card>
              )}
            </div>
          </TabsContent>

          {/* PRICE SHEET TAB */}
          <TabsContent value="sheet" className="mt-4">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base">Generate Price Sheet</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div><label className="text-sm font-medium mb-1 block">Unit ID</label><Input value={sheetForm.unit_id} onChange={(e) => setSheetForm({ ...sheetForm, unit_id: e.target.value })} placeholder="unit_abc123" data-testid="sheet-unit-id" /></div>
                  <Button className="w-full" onClick={generateSheet} disabled={!sheetForm.unit_id} data-testid="sheet-btn">
                    <FileText className="w-4 h-4 mr-2" /> Generate
                  </Button>
                </CardContent>
              </Card>

              {sheetResult ? (
                <Card className="border-2 border-emerald-200">
                  <CardHeader><CardTitle className="text-base">Price Sheet</CardTitle></CardHeader>
                  <CardContent className="space-y-3" data-testid="sheet-result">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="font-semibold text-slate-900">{sheetResult.project?.name}</p>
                      <p className="text-xs text-slate-500">{sheetResult.project?.location} | RERA: {sheetResult.project?.rera || "N/A"}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs text-blue-600 mb-1">Unit Details</p>
                      <p className="text-sm">{sheetResult.unit?.tower}-{sheetResult.unit?.floor}F | {sheetResult.unit?.configuration} | {sheetResult.unit?.super_buildup_area || sheetResult.unit?.carpet_area} sqft | {sheetResult.unit?.facing} facing</p>
                    </div>
                    <div className="space-y-1 text-sm">
                      <Row label="Base Price" val={formatINR(sheetResult.pricing?.base_price)} />
                      <Row label="Floor Rise" val={formatINR(sheetResult.pricing?.floor_rise)} />
                      <Row label="Facing Premium" val={formatINR(sheetResult.pricing?.facing_premium)} />
                      <Row label="Parking" val={formatINR(sheetResult.pricing?.parking)} />
                      <div className="border-t pt-1"><Row label="Agreement Value" val={formatINR(sheetResult.pricing?.agreement_value)} bold /></div>
                      <Row label="GST" val={formatINR(sheetResult.pricing?.gst)} />
                      <Row label="Stamp Duty" val={formatINR(sheetResult.pricing?.stamp_duty)} />
                      <Row label="Registration" val={formatINR(sheetResult.pricing?.registration)} />
                    </div>
                    <div className="bg-slate-900 text-white rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-300">Total Cost</p>
                      <p className="text-xl font-bold">{formatINR(sheetResult.pricing?.total_cost)}</p>
                      <p className="text-xs text-slate-400">{formatINR(sheetResult.pricing?.price_per_sqft)}/sqft</p>
                    </div>
                    {sheetResult.payment_plans?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-2">Payment Plans</p>
                        {sheetResult.payment_plans.map((p, i) => (
                          <div key={i} className="bg-emerald-50 rounded-lg p-2 mb-1 border border-emerald-100">
                            <p className="text-sm font-medium text-emerald-800">{p.name}</p>
                            <p className="text-xs text-slate-600">{p.description}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card><CardContent className="p-12 text-center text-slate-400"><FileText className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>Enter a unit ID to generate a price sheet</p></CardContent></Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Add Rule Dialog */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-rule-dialog">
            <DialogHeader><DialogTitle>Create Pricing Rule</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-sm font-medium mb-1 block">Project</label>
                <Select value={form.project_id} onValueChange={(v) => {
                  const p = projects.find(p => p.project_id === v);
                  setForm({ ...form, project_id: v, project_name: p?.name || "" });
                }}>
                  <SelectTrigger data-testid="rule-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-sm font-medium mb-1 block">Base Price/sqft</label><Input type="number" value={form.base_price_per_sqft} onChange={(e) => setForm({ ...form, base_price_per_sqft: e.target.value })} data-testid="rule-base-price" /></div>
                <div><label className="text-sm font-medium mb-1 block">Floor Rise/floor/sqft</label><Input type="number" value={form.floor_rise_per_floor} onChange={(e) => setForm({ ...form, floor_rise_per_floor: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-sm font-medium mb-1 block">Rise Starts at Floor</label><Input type="number" value={form.floor_rise_start} onChange={(e) => setForm({ ...form, floor_rise_start: e.target.value })} /></div>
                <div><label className="text-sm font-medium mb-1 block">Parking/slot</label><Input type="number" value={form.parking_charge} onChange={(e) => setForm({ ...form, parking_charge: e.target.value })} /></div>
              </div>
              <p className="text-xs font-medium text-slate-500 mt-2">Facing Premiums (per sqft)</p>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-xs text-slate-500 mb-1 block">Garden</label><Input type="number" value={form.facing_garden} onChange={(e) => setForm({ ...form, facing_garden: e.target.value })} /></div>
                <div><label className="text-xs text-slate-500 mb-1 block">Park</label><Input type="number" value={form.facing_park} onChange={(e) => setForm({ ...form, facing_park: e.target.value })} /></div>
                <div><label className="text-xs text-slate-500 mb-1 block">Road</label><Input type="number" value={form.facing_road} onChange={(e) => setForm({ ...form, facing_road: e.target.value })} /></div>
              </div>
              <p className="text-xs font-medium text-slate-500 mt-2">Taxes & Charges</p>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-xs text-slate-500 mb-1 block">GST %</label><Input type="number" value={form.gst_pct} onChange={(e) => setForm({ ...form, gst_pct: e.target.value })} /></div>
                <div><label className="text-xs text-slate-500 mb-1 block">Stamp Duty %</label><Input type="number" value={form.stamp_duty_pct} onChange={(e) => setForm({ ...form, stamp_duty_pct: e.target.value })} /></div>
                <div><label className="text-xs text-slate-500 mb-1 block">Registration %</label><Input type="number" value={form.registration_pct} onChange={(e) => setForm({ ...form, registration_pct: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-xs text-slate-500 mb-1 block">Legal Charges</label><Input type="number" value={form.legal_charges} onChange={(e) => setForm({ ...form, legal_charges: e.target.value })} /></div>
                <div><label className="text-xs text-slate-500 mb-1 block">Maintenance Dep.</label><Input type="number" value={form.maintenance_deposit} onChange={(e) => setForm({ ...form, maintenance_deposit: e.target.value })} /></div>
                <div><label className="text-xs text-slate-500 mb-1 block">Infra Charges</label><Input type="number" value={form.infra_charges} onChange={(e) => setForm({ ...form, infra_charges: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={createRule} disabled={!form.project_id} data-testid="rule-save-btn">Create Rule</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Rule Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-rule-dialog">
            <DialogHeader><DialogTitle>Edit Pricing Rule</DialogTitle></DialogHeader>
            {editForm && (
              <div className="grid grid-cols-2 gap-3 py-2">
                <div><label className="text-sm font-medium mb-1 block">Base Price/sqft</label><Input type="number" value={editForm.base_price_per_sqft || 0} onChange={e => setEditForm({...editForm, base_price_per_sqft: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Floor Rise/floor/sqft</label><Input type="number" value={editForm.floor_rise_per_floor || 0} onChange={e => setEditForm({...editForm, floor_rise_per_floor: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Parking Charge</label><Input type="number" value={editForm.parking_charge || 0} onChange={e => setEditForm({...editForm, parking_charge: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">GST %</label><Input type="number" value={editForm.gst_pct || 0} onChange={e => setEditForm({...editForm, gst_pct: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Stamp Duty %</label><Input type="number" value={editForm.stamp_duty_pct || 0} onChange={e => setEditForm({...editForm, stamp_duty_pct: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Registration %</label><Input type="number" value={editForm.registration_pct || 0} onChange={e => setEditForm({...editForm, registration_pct: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Garden Premium/sqft</label><Input type="number" value={editForm.facing_garden || 0} onChange={e => setEditForm({...editForm, facing_garden: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Park Premium/sqft</label><Input type="number" value={editForm.facing_park || 0} onChange={e => setEditForm({...editForm, facing_park: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Road Premium/sqft</label><Input type="number" value={editForm.facing_road || 0} onChange={e => setEditForm({...editForm, facing_road: e.target.value})} /></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={saveEditRule} data-testid="save-edit-rule-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const Row = ({ label, val, sub, bold }) => (
  <div className="flex justify-between items-baseline">
    <div>
      <span className={`text-slate-600 ${bold ? "font-semibold text-slate-800" : ""}`}>{label}</span>
      {sub && <span className="text-xs text-slate-400 ml-1">({sub})</span>}
    </div>
    <span className={`${bold ? "font-bold text-slate-900" : "text-slate-700"}`}>{val}</span>
  </div>
);

export default PricingPage;
